import {MIN_HOURS_FOR_INFERENCE} from '../../const'
import UIUtils from '../../utils/UIUtils'
import DataUtils from '../../utils/DataUtils'
import Router from '../../router'
import BackendProvider from '../../services/BackendProvider'
import BackendService from '../../services/BackendService'
import LoggerService from '../../services/LoggerService'
import Vue from 'vue'
import Utils from '../../utils/Utils'

export default {
    namespaced: true,
    state: () => ({
        selectedPatient: null,
        selectedSlide: null,
        focusedWell: null,
        hoveredWellId: null,
        shareDialogOpened: false,
        shareToPatientDialogOpened: false,
        homePageSelectedView: 'ongoing',
        videoPlaybackReady: false,
        selectedHelpCenterTab: null,
        showAnnotationGraphics: false,
        showAdjustGardnerDialog: false,
        selectedForComparison: [],
        instruments: [],
        patients: [],
        pastSlides: [],
        isPastSlideLoading: false,
        pastSlideMetadata: null,
        pastSlideFilter: null,
        wells: [],
        selectedTreatment: null,
        noteIsEdited: false,
        triggerWellReFetch: false,
        pastSlidesTotalCountWithoutFilter: 0
    }),
    getters: {
        getTreatmentWell: state => wellId => {
            if (!state.selectedTreatment) return null

            return state.selectedTreatment.wellsByIndex.find(well => well && well._id === wellId)
        },
        activeSlideNonEmptyWells: state => {
            if (!state.selectedSlide) return []

            return state.selectedSlide.wells.filter(well => !!well)
        },
        fertMethodNeedsApproval: state => {
            if (!state.selectedTreatment) return false

            return !state.selectedTreatment.wellsByIndex.every(well => well.insemination.isApproved)
        },
        getSlideWell: state => wellId => {
            if (!state.selectedSlide) return null

            return state.selectedSlide.wells.find(well => well && well._id === wellId)
        },
        getWellIndexByWellId: state => wellId => {
            const {wellsByIndex = []} = state.selectedTreatment
            return wellsByIndex.findIndex(well => well._id === wellId)
        },
        topRankedWells: state => {
            if (!state.selectedTreatment) return []

            return state.selectedTreatment.wellsByIndex
                .filter(well => well.kidRank <= 3)
                .sort((a, b) => a.kidRank - b.kidRank)
        },
        activePatientsCount: state => viewOption => {
            const patients = new Set()

            state.instruments
                .flatMap(instrument => instrument.slides)
                .forEach(slide => {
                    if (slide) patients.add(slide.patientId)
                })

            return patients.size
        },
        getPnCount: () => well => {
            return DataUtils.getInferenceOverridableField(well, 'pnCount.value')
        },
        getPnCountTime: () => well => {
            return DataUtils.getInferenceOverridableField(well, 'pnCount.time')
        },
        getPnImageUrl: (state, getters) => well => {
            const countTime = getters.getPnCountTime(well)
            const pnCountTime = countTime ? parseFloat(countTime) : 0.0

            const pnCount = getters.getPnCount(well)
            const hasPN = pnCount && pnCount > 0 && pnCountTime

            if (hasPN) {
                const pnRunIndex = well.runs.findLastIndex(
                    run => !!run && run.time === Number(pnCountTime.toFixed(2))
                )

                const {videoFramesTime = []} = well

                if (pnRunIndex >= 0 && pnRunIndex < videoFramesTime.length) {
                    return Utils.getImageUrlFromTime(well, videoFramesTime[pnRunIndex])
                }
            }

            return ''
        },
        getPnText: (state, getters, rootState, rootGetters) => well => {
            const count = getters.getPnCount(well)

            if (count === null) return 'Pending'
            return count
        },
        getPnHazard: (state, getters) => well => {
            const count = getters.getPnCount(well)

            // video started with more advanced stage
            const isNotRelevant = count === null
            const isHealthy = count === 2

            return !(isNotRelevant || isHealthy)
        },
        checkDucsHazard: () => well => {
            const {duc} = well.inferenceOverridingData
            const isOverridden = duc && duc.hasOwnProperty('duc1')

            if (isOverridden) {
                return duc.duc1
            }

            const {duc1, duc2, duc3} = well.inferenceData.duc
            return !!(duc1 || duc2 || duc3)
        },
        checkDegeneratedOocyteHazard: () => well => {
            const isOverridden = well.inferenceOverridingData.hasOwnProperty('degeneratedOocyte')

            if (isOverridden) {
                return well.inferenceOverridingData.degeneratedOocyte
            }

            return !!well.inferenceData.degeneratedOocyte
        },
        checkFragmentation: () => well => {
            return well.inferenceData.morphokinetics.find(mkEvent => mkEvent.fragmented)
        },
        getHazards: (state, getters) => well => {
            const hazards = []
            if (getters.checkDucsHazard(well)) hazards.push('DUC')
            if (getters.checkDegeneratedOocyteHazard(well)) hazards.push('DEG')
            if (getters.checkFragmentation(well)) hazards.push('FRAG')

            return hazards
        },
        availableFocals: state => {
            return state.selectedTreatment ? state.selectedTreatment.availableFocals : []
        },
        isTooEarlyForScores: state => {
            if (!state.selectedTreatment) return null

            return state.selectedTreatment.hoursSinceFertilization < MIN_HOURS_FOR_INFERENCE
        },
        getTreatment: state => {
            if (!state.selectedTreatment) return null

            return state.selectedTreatment
        },
        getClinicGardnerLabel: (state, getters, rootState, rootGetters) => gardnerScore => {
            const $translate = rootGetters['locale/translate']

            if (!gardnerScore) return $translate('na')

            const {expansion, icm, te} = gardnerScore

            return `${expansion}${icm}${te}`
        },
        getMaxDayForFilter: state => {
            const days = state.instruments.flatMap(instrument =>
                instrument.slides.map(slide =>
                    Utils.getDaysSinceFertilization(slide.hoursSinceFertilization)
                )
            )
            if (days && days.length) {
                return Math.max(...days)
            }
            return 0
        },
        getStartingHourOfSelectedSlide: state => {
            if (!state.selectedSlide || !state.selectedSlide.wells) return 0.0

            const hours = state.selectedSlide.wells
                .filter(well => !!well)
                .map(well => well.runs.find(run => !!run).time)
            if (hours && hours.length) {
                return Math.min(...hours)
            }
            return 0.0
        },
        getSelectedInstrument: state => {
            const {
                selectedSlide,
                selectedTreatment: {instruments = []}
            } = state
            if (selectedSlide && instruments.length) {
                return instruments.find(instrument => instrument._id === selectedSlide.instrumentId)
            }
            return {}
        },
        getMasterWell: state => {
            if (!(state.selectedSlide && state.selectedSlide.wells)) {
                return null
            }
            let well = state.selectedSlide.wells.find(well => !!well)
            let maxEvents = 0
            for (let i = 0; i < state.selectedSlide.wells.length; i++) {
                if (state.selectedSlide.wells[i] && state.selectedSlide.wells[i].mkEventsMap) {
                    const _maxEvent = Object.entries(
                        state.selectedSlide.wells[i].mkEventsMap
                    ).filter(([_, value]) => value).length
                    if (_maxEvent > maxEvents) {
                        well = {...state.selectedSlide.wells[i]}
                        maxEvents = _maxEvent
                    }
                }
            }
            return well
        }
    },
    mutations: {
        instruments(state, instruments) {
            state.instruments = instruments
        },
        patients(state, patients) {
            state.patients = patients
        },
        pastSlides(state, slides) {
            state.pastSlides = slides
        },
        homePageSelectedView(state, view) {
            state.homePageSelectedView = view
        },
        setPastSlideFilter(state, filter) {
            state.pastSlideFilter = filter
        },
        setPastSlidesMetadata(state, metadata) {
            state.pastSlideMetadata = metadata
        },
        setPastSlidesTotalCountWithoutFilter(state, totalCount) {
            state.pastSlidesTotalCountWithoutFilter = totalCount
        },
        setSelectedPatient(state, patient) {
            state.selectedPatient = patient
        },
        selectedTreatment(state, treatment) {
            state.selectedTreatment = treatment
        },
        setSelectedSlide(state, slide) {
            state.selectedSlide = slide

            if (slide) {
                state.selectedTreatment.hoursSinceFertilization = slide.hoursSinceFertilization
            }

            if (slide && Router.currentRoute.query.slide_id !== slide._id) {
                Router.update({slide_id: slide._id})
            }
        },
        focusedWell(state, well) {
            if (well) {
                // default video settings
                well.zoom = 1
                well.focal = 0
                well.brightness = 1
                well.invert = false
            }

            state.focusedWell = well
        },
        wellZoom(state, zoom) {
            state.focusedWell.zoom = zoom
        },
        wellFocal(state, focal) {
            state.focusedWell.focal = focal
        },
        wellBrightness(state, brightness) {
            state.focusedWell.brightness = brightness
        },
        wellInvert(state, invert) {
            state.focusedWell.invert = invert
        },
        showAnnotationGraphics(state, status) {
            state.showAnnotationGraphics = status
        },
        selectedForComparison(state, wells) {
            state.selectedForComparison = wells
        },
        noteIsEdited(state, isEdited) {
            state.noteIsEdited = isEdited
        },
        videoPlaybackReady(state, isReady) {
            state.videoPlaybackReady = isReady
        },
        triggerWellReFetch(state, status) {
            state.triggerWellReFetch = status
        },
        shareDialogOpened(state, dialogOpened) {
            state.shareDialogOpened = dialogOpened
        },
        shareToPatientDialogOpened(state, dialogOpened) {
            state.shareToPatientDialogOpened = dialogOpened
        }
    },
    actions: {
        async updateTreatmentAge({state, commit}, {treatmentId, delta}) {
            state.instruments.forEach(instrument => {
                if (instrument.slides) {
                    instrument.slides.forEach(slide => {
                        if (slide.treatment && slide.treatment._id === treatmentId) {
                            if (delta.oocyteAge) {
                                slide.treatment.oocyteAge = delta.oocyteAge
                            }
                            if (delta.patientAge) {
                                slide.treatment.patientAge = delta.patientAge
                            }
                        }
                    })
                }
            })
        },
        async setPatients({rootState, commit}) {
            const patients = await BackendProvider.fetchPatients()
            BackendService.extendPatients(patients, rootState.common.user)

            commit('patients', patients)
        },
        async setInstruments({state, commit}, tenantId) {
            const instruments = await BackendProvider.fetchInstruments(tenantId)
            BackendService.extendInstruments(instruments)

            commit('instruments', instruments)
        },
        async setPastSlides({state, commit, rootState}) {
            let freeSearchText = rootState.filter.filterText
            let hoursSinceFertilization = Utils.getDaysSinceFertilizationRange(
                rootState.filter.filterDays
            )
            freeSearchText = freeSearchText.length === 0 ? null : freeSearchText
            hoursSinceFertilization =
                hoursSinceFertilization == false ? null : JSON.stringify(hoursSinceFertilization)

            UIUtils.showLoadingMask()
            const {dateRange = {}} = state.pastSlideFilter || {}
            const {startDate: start, endDate: end} = dateRange

            const slides = await BackendProvider.fetchPastSlides({
                start,
                end,
                freeSearchText,
                hoursSinceFertilization
            })
            BackendService.extendPastSlides(slides.slideCards)

            commit('setPastSlidesMetadata', slides.metadata)
            commit('pastSlides', slides.slideCards)

            if (state.pastSlidesTotalCountWithoutFilter == 0) {
                commit('setPastSlidesTotalCountWithoutFilter', slides.metadata.totalCount)
            }

            UIUtils.hideLoadingMask()
        },
        async loadMoreSlides({state, commit, rootState}) {
            let freeSearchText = rootState.filter.filterText
            let hoursSinceFertilization = Utils.getDaysSinceFertilizationRange(
                rootState.filter.filterDays
            )
            freeSearchText = freeSearchText.length === 0 ? null : freeSearchText
            hoursSinceFertilization =
                hoursSinceFertilization == false ? null : JSON.stringify(hoursSinceFertilization)
            const {page, pageSize, pageCount} = state.pastSlideMetadata
            if (page >= pageCount) return

            state.isPastSlideLoading = true

            const {dateRange = {}} = state.pastSlideFilter || {}
            const {startDate: start, endDate: end} = dateRange

            const slides = await BackendProvider.fetchPastSlides({
                start,
                end,
                freeSearchText,
                hoursSinceFertilization,
                page: page + 1,
                pageSize
            })

            BackendService.extendPastSlides(slides.slideCards)
            commit('setPastSlidesMetadata', slides.metadata)
            commit('pastSlides', [...state.pastSlides, ...slides.slideCards])

            state.isPastSlideLoading = false
        },
        async setWellData({state, commit, dispatch}, {forceFullReload, route}) {
            UIUtils.showLoadingMask()

            const {well_id, treatment_id, patient_id} = route.params
            LoggerService.navigationEvent('goto_well_page', well_id)

            if (forceFullReload || !state.selectedTreatment || !state.selectedTreatment._id) {
                await dispatch('loadPatientData', patient_id)
                await dispatch('setSelectedTreatment', treatment_id)
            }

            const well = await BackendProvider.fetchWell(well_id)
            BackendService.extendWell(well)

            const {slides} = state.selectedTreatment
            const slide = slides.find(_slide => _slide._id === well.slideId)

            commit('focusedWell', well)
            commit('setSelectedSlide', slide)
            commit('triggerWellReFetch', false)

            well.notes = await BackendProvider.fetchNotes(well._id)

            UIUtils.hideLoadingMask()
        },
        async loadPatientData({rootState, commit}, patientId) {
            const patient = await BackendProvider.fetchPatient(patientId)

            BackendService.preparePatient(patient, rootState.common.user.tenants)

            commit('setSelectedPatient', patient)

            const {tenant} = patient
            Vue.$ga.set('selectedTenant', tenant.name)
            commit('common/setSelectedTenant', tenant, {root: true})
        },
        addSelectedForComparison({state, commit, dispatch}, wellId) {
            const isInComparison = state.selectedForComparison.includes(wellId)

            if (!isInComparison) {
                commit('selectedForComparison', [...state.selectedForComparison, wellId])
                dispatch('compileCompareQueryParams')
            }
        },
        setSelectedForComparison({commit, dispatch}, wellIds) {
            commit('selectedForComparison', wellIds)
            dispatch('compileCompareQueryParams')
        },
        removeSelectedForComparison({state, dispatch}, wellId) {
            const index = state.selectedForComparison.indexOf(wellId)
            if (index > -1) {
                state.selectedForComparison.splice(index, 1)
                dispatch('compileCompareQueryParams')
            }
        },
        addRemoveFromWellComparison({dispatch}, {wellId, add}) {
            if (add) {
                dispatch('addSelectedForComparison', wellId)
            } else {
                dispatch('removeSelectedForComparison', wellId)
            }
        },
        async setSelectedTreatment({state, commit, dispatch}, treatmentId) {
            const treatment = treatmentId ? await BackendProvider.fetchTreatment(treatmentId) : null

            if (treatment) {
                BackendService.extendTreatment(treatment)
            }

            commit('selectedTreatment', treatment)

            const areWellsRelevantToTreatment =
                treatment &&
                treatment.wellsByIndex &&
                treatment.wellsByIndex.find(well => well._id === state.selectedForComparison[0])

            if (!areWellsRelevantToTreatment) {
                dispatch('setSelectedForComparison', [])
            }
        },
        async updateTreatment({state, commit}, {treatmentId, payload}) {
            UIUtils.showLoadingMask()
            const treatment = await BackendProvider.updateTreatment(treatmentId, payload)
            if (treatment) {
                BackendService.extendTreatment(treatment)
            }
            commit('selectedTreatment', treatment)
            UIUtils.hideLoadingMask()
        },
        compileCompareQueryParams({state}) {
            const sfc = state.selectedForComparison.join(',')

            const queryClone = {...Router.currentRoute.query}

            if (!sfc && queryClone.sfc) {
                delete queryClone.sfc
                Router.push({query: queryClone})
            } else if (sfc && sfc !== queryClone.sfc) {
                Router.push({query: {...queryClone, sfc}})
            }
        },
        resetSelectedItems({commit, dispatch}) {
            commit('setSelectedPatient', null)
            dispatch('setSelectedTreatment', null)
            commit('setSelectedSlide', null)
            commit('focusedWell', null)
        },
        resetSession({state, commit, dispatch}) {
            dispatch('resetSelectedItems')

            state.hoveredWellId = null
            commit('videoPlaybackReady', false)
            commit('showAnnotationGraphics', false)
            state.homePageSelectedView = 'ongoing'
            state.shareDialogOpened = false
            state.showAdjustGardnerDialog = false
            commit('selectedForComparison', [])
            commit('instruments', [])
            commit('patients', [])
            state.slides = []
            state.wells = []
        }
    }
}
